import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

interface Memory {
  id: number
  name: string
  description: string
  memory_type: string
  priority: number
  hook_keywords: string[]
  active: boolean
  injection_count: number
  search_count: number
  hook_count: number
  project_id: number
  project_name: string
  created_at: string
  updated_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  memories: Memory[]
  pagy: Pagination
  filters: {
    active: string
    project_name?: string
    memory_type?: string
    name_or_description_cont?: string
    s?: string
  }
  valid_memory_types: string[]
  stats: { active: number; inactive: number; total: number; projects_count: number }
}

const PRIORITY_LABELS: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High' }
const PRIORITY_COLORS: Record<number, string> = {
  1: 'secondary',
  2: 'default',
  3: 'warning',
}

function Index({ memories, pagy, filters, stats, valid_memory_types }: Props) {
  const [searchQuery, setSearchQuery] = useState(filters.name_or_description_cont || '')
  const [projectName, setProjectName] = useState(filters.project_name || '')

  const basePath = '/admin/collective_knowledge/memories'

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}
    if (filters.active && filters.active !== 'all') params.active = filters.active
    if (projectName) params.project_name = projectName
    if (filters.memory_type) params.memory_type = filters.memory_type
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery

    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleFilter = (key: string, value: string) => {
    const params = buildParams({ [key]: value === 'all' ? null : value })
    router.get(basePath, params, { preserveState: true })
  }

  const handleSearch = () => {
    router.get(basePath, buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearchQuery('')
    setProjectName('')
    router.get(basePath, {}, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) newSort = `${field} desc`
    router.get(basePath, { ...buildParams(), 'q[s]': newSort }, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort === `${field} asc`) return '↑'
    if (currentSort === `${field} desc`) return '↓'
    return '⇅'
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                Project Memories ({pagy.count})
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Badge variant="success" className="text-[10px] px-1 py-0">{stats.active}</Badge>
                <span>/</span>
                <Badge variant="secondary" className="text-[10px] px-1 py-0">{stats.inactive} inactive</Badge>
                <span>across</span>
                <Badge variant="outline" className="text-[10px] px-1 py-0">{stats.projects_count} projects</Badge>
              </div>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.active || 'true'}
                onValueChange={(v) => handleFilter('active', v)}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="text"
                placeholder="Filter by project..."
                className="h-7 text-xs w-40"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Select
                value={filters.memory_type || 'all'}
                onValueChange={(v) => handleFilter('memory_type', v)}
              >
                <SelectTrigger className="h-7 text-xs w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {valid_memory_types.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder="Search name or description..."
                className="h-7 text-xs w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">Search</Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">Reset</Button>
            </div>

            {/* Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Name</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Project</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Type</th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('priority')}
                      >
                        <div className="flex items-center gap-1">
                          Priority
                          <span className="text-gray-400">{getSortIcon('priority')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Keywords</th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Status</th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('injection_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Inj
                          <span className="text-gray-400">{getSortIcon('injection_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('search_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Search
                          <span className="text-gray-400">{getSortIcon('search_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('hook_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Hooks
                          <span className="text-gray-400">{getSortIcon('hook_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('updated_at')}
                      >
                        <div className="flex items-center gap-1">
                          Updated
                          <span className="text-gray-400">{getSortIcon('updated_at')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {memories.length > 0 ? (
                      memories.map((memory) => (
                        <tr key={memory.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">{memory.name}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-md">{memory.description}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-foreground">{memory.project_name}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant="secondary" className="text-xs">{memory.memory_type}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant={PRIORITY_COLORS[memory.priority] as any} className="text-xs">
                              {PRIORITY_LABELS[memory.priority]}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5">
                            <div className="flex flex-wrap gap-0.5 max-w-48">
                              {memory.hook_keywords.slice(0, 3).map((kw) => (
                                <Badge key={kw} variant="outline" className="text-[10px] px-1 py-0">{kw}</Badge>
                              ))}
                              {memory.hook_keywords.length > 3 && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">+{memory.hook_keywords.length - 3}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant={memory.active ? 'success' : 'destructive'}>
                              {memory.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{memory.injection_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{memory.search_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{memory.hook_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(memory.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`${basePath}/${memory.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          No memories found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        Previous
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
